import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import CloseIcon from '@mui/icons-material/Close';
import {
  DataGridPremium,
  GridToolbarQuickFilter,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarColumnsButton
} from '@mui/x-data-grid-premium';
import { MdVisibility } from 'react-icons/md';
import { useFetchData } from 'services/queries';
import { formatCurrency } from 'utils/formatters';
import dayjs from 'dayjs';
import Chip from '@mui/material/Chip';
import { PaymentStatusChip } from '../../PaymentStatusChip';
import { PAYMENT_CATEGORY_CONFIG, PAYMENT_STATUS } from '../../constants';

// Custom toolbar for the dialog grid
const CustomToolbar = () => {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={2}
      py={1}
    >
      <Box color="secondary" display="flex" justifyContent="flex-end" alignItems="flex-start">
        <GridToolbarQuickFilter />
      </Box>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={3}
      >
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarExport />
      </Stack>
    </Stack>
  );
};

const PaymentDetailsDialog = ({ open, onClose, title, filterParams }) => {
  const navigate = useNavigate();
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0
  });

  // Fetch individual payments based on filter params
  const endpoint = filterParams ? `/payment/?${filterParams}` : '/payment/';
  const { data: payments = [], isLoading } = useFetchData(
    endpoint,
    true,
    open
  );

  if (!open) return null;

  const columns = [
    {
      field: 'payment_id',
      headerName: 'Payment #',
      width: 90,
      type: 'number',
      cellClassName: 'v-center-cell',
      renderCell: (params) => {
        if (!params || !params.value) return '-';
        return (
          <Typography variant="body2" fontWeight="bold">
            #{params.value}
          </Typography>
        );
      }
    },
    {
      field: 'sales_order_order_identifier',
      headerName: 'Order ID',
      width: 100,
      cellClassName: 'v-center-cell',
      renderCell: (params) => {
        if (!params) return null;
        return (
          <Typography variant="body2" fontWeight="medium">
            {params.value || '-'}
          </Typography>
        );
      }
    },
    {
      field: 'sales_order_buyer_social_reason',
      headerName: 'Client',
      minWidth: 150,
      flex: 1,
      cellClassName: 'v-center-cell'
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      cellClassName: 'v-center-cell',
      type: 'singleSelect',
      valueOptions: Object.values(PAYMENT_STATUS).filter(s => s !== PAYMENT_STATUS.OVERDUE),
      valueGetter: (_, row) => row.effective_status || row.status || '',
      renderCell: (params) => {
        if (!params || !params.row) return '-';
        const {
          status,
          effective_status,
          is_overdue,
          paid_late,
          days_late
        } = params.row;

        if (!status) return '-';

        return (
          <PaymentStatusChip
            status={status}
            effective_status={effective_status}
            is_overdue={is_overdue}
            paid_late={paid_late}
            days_late={days_late}
          />
        );
      }
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 120,
      cellClassName: 'v-center-cell',
      type: 'singleSelect',
      valueOptions: ['INVOICE', 'FREIGHT_FEES', 'TAX_DUTIES'],
      renderCell: (params) => {
        if (!params || !params.row) return '-';
        const { category, category_display } = params.row;

        if (!category) {
          return (
            <Chip
              label="Uncategorized"
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          );
        }

        const config = PAYMENT_CATEGORY_CONFIG[category];
        return (
          <Chip
            label={category_display || config?.label || category}
            size="small"
            color={config?.color || 'default'}
            sx={{ fontSize: '0.7rem' }}
          />
        );
      }
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      width: 100,
      type: 'date',
      cellClassName: 'v-center-cell',
      valueGetter: (_, row) => {
        return row.due_date ? dayjs(row.due_date).toDate() : null;
      },
      renderCell: (params) => {
        if (!params || !params.row || !params.row.due_date) return '-';

        const date = dayjs(params.row.due_date).toDate();
        const isOverdue = params.row.is_overdue;

        return (
          <Typography
            variant="body2"
            color={isOverdue ? 'error' : 'text.primary'}
            fontWeight={isOverdue ? 'bold' : 'normal'}
          >
            {dayjs(date).format('DD/MM/YYYY')}
          </Typography>
        );
      }
    },
    {
      field: 'usd_value',
      headerName: 'USD',
      width: 110,
      type: 'number',
      cellClassName: 'v-center-cell',
      valueGetter: (_, row) => parseFloat(row.usd_value || 0),
      renderCell: (params) => {
        if (!params || params.value == null) return '-';
        return formatCurrency(params.value, 'USD');
      }
    },
    {
      field: 'brl_value',
      headerName: 'BRL',
      width: 110,
      type: 'number',
      cellClassName: 'v-center-cell',
      valueGetter: (_, row) => parseFloat(row.brl_value || 0),
      renderCell: (params) => {
        if (!params || params.value == null) return '-';
        return formatCurrency(params.value, 'BRL');
      }
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      disableExport: true,
      cellClassName: 'v-center-cell',
      renderCell: (params) => {
        if (!params || !params.row) return null;

        return (
          <Tooltip title="View Order">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
                navigate(`/admin/payment/order/${params.row.sales_order}?paymentId=${params.row.payment_id}`);
              }}
              sx={{ color: 'primary.main' }}
            >
              <MdVisibility size={18} />
            </IconButton>
          </Tooltip>
        );
      }
    }
  ];

  // Row coloring based on payment status
  const getRowClassName = (params) => {
    if (!params || !params.row) return '';

    if (params.row.effective_status === PAYMENT_STATUS.OVERDUE) {
      return 'overdue-row';
    }

    if (params.row.status === PAYMENT_STATUS.PAID) {
      return 'paid-row';
    }

    return '';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {title}
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        <Box sx={{ height: 500, width: '100%' }}>
          <DataGridPremium
            rows={payments}
            columns={columns}
            loading={isLoading}
            pagination
            disableRowSelectionOnClick
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            getRowClassName={getRowClassName}
            initialState={{
              sorting: {
                sortModel: [{ field: 'due_date', sort: 'asc' }]
              },
              aggregation: {
                model: {
                  usd_value: 'sum',
                  brl_value: 'sum'
                }
              }
            }}
            pageSizeOptions={[10, 25, 50]}
            onRowClick={(params) => {
              onClose();
              navigate(`/admin/payment/order/${params.row.sales_order}?paymentId=${params.row.payment_id}`);
            }}
            slots={{
              toolbar: CustomToolbar
            }}
            sx={{
              '& .v-center-cell': {
                display: 'flex',
                alignItems: 'center'
              },
              '& .MuiDataGrid-cell': {
                borderBottom: 'none'
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'background.paper',
                borderBottom: 2,
                borderColor: 'divider'
              },
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              },
              '& .overdue-row': {
                backgroundColor: '#FFF5F5',
                '&:hover': {
                  backgroundColor: '#FFE5E5 !important',
                }
              },
              '& .paid-row': {
                backgroundColor: '#F0FDF4',
                '&:hover': {
                  backgroundColor: '#E5F7ED !important',
                }
              }
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentDetailsDialog;
